import { format } from 'date-fns';

export type KeycloakTokenCallback = (
  token: KeycloakToken | null,
  error?: unknown
) => Promise<void>;

export interface KeycloakToken {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  expiry?: number;
}

export interface KeycloakState {
  currentToken: KeycloakToken | null;
  refreshTimeout?: NodeJS.Timeout;
  callback?: KeycloakTokenCallback;
}

const TOKEN_KEY = 'deinverband:keycloakToken';
const LOCK_KEY = 'deinverband:keycloakRefreshLock';

/**
 * Global keycloak configuration
 */
const keycloakConfig = {
  initialized: false,
  tokenKey: '',
  lockKey: '',
  clientId: '',
  baseUrl: '',
  realm: ''
};

/**
 * Current keycloak state
 */
const keycloakState: KeycloakState = {
  currentToken: null
};

export async function keycloakFetch<T>(
  url: string,
  body: Record<string, string> = {},
  token?: KeycloakToken | null
): Promise<T> {
  // provide the client id
  const params = new URLSearchParams({
    client_id: keycloakConfig.clientId,
    ...body
  });

  // provide authentication
  const authHeader: Record<string, string> = token
    ? { Authorization: `Bearer ${token.access_token}` }
    : {};

  const res = await fetch(
    `${keycloakConfig.baseUrl}/realms/${keycloakConfig.realm}/protocol/openid-connect/${url}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...authHeader
      },
      body: params.toString()
    }
  );

  // validate the response
  if (!res.ok || res.status < 200 || res.status >= 300)
    throw new Error(`Request failed ${res.status}:\n${res.statusText}`);
  return (await res.json()) as T;
}

export const fetchTokenWithEmailAndPassword = (
  email: string,
  password: string
) => {
  return keycloakFetch<KeycloakToken>('token', {
    grant_type: 'password',
    username: email,
    password
  });
};

const fetchLogout = async () => {
  await keycloakFetch('logout', {}, keycloakState.currentToken);
  // clear current token
  return null;
};

const fetchTokenWithRefreshToken = (refreshToken: string) => {
  return keycloakFetch<KeycloakToken>('token', {
    grant_type: 'refresh_token',
    refresh_token: refreshToken
  });
};

const withExpiry = (token: KeycloakToken | null) => {
  if (token?.expires_in && !token.expiry)
    token.expiry = new Date().getTime() + token.expires_in * 1000;
  return token;
};

const logToken = (token: KeycloakToken | null, source: string) => {
  if (token)
    console.info(
      `[keycloak-lib] Token received from ${source} ${
        token?.expiry
          ? `(valid until ${format(token.expiry, 'Pp')})`
          : '(no expiry)'
      }`
    );
  else console.info(`[keycloak-lib] No token received from ${source}`);
  // syntactic sugar
  return token;
};

/**
 * Safely await the given promise and forward to the callback
 */
const handleTokenPromise = (
  result: Promise<KeycloakToken | null>,
  source: string
): Promise<void> => {
  return result
    .then(async (token) => {
      // ensure token has absolute expiry time
      token = logToken(withExpiry(token), source);

      // refresh the token if neccessary
      if (token) token = await refreshTokenWhenNeeded(token);

      localStorage.setItem(keycloakConfig.tokenKey, JSON.stringify(token));
      keycloakState.currentToken = token;
      return keycloakState.callback?.(token);
    })
    .catch((error) => {
      console.error('[keycloak-lib] Failed to retrieve token', error);

      localStorage.setItem(keycloakConfig.tokenKey, JSON.stringify(null));
      keycloakState.currentToken = null;
      return keycloakState.callback?.(null, error);
    });
};

const cancelTokenRefresh = () => {
  if (keycloakState.refreshTimeout) {
    console.info('[keycloak-lib] Token refresh cancelled');
    clearTimeout(keycloakState.refreshTimeout);
    keycloakState.refreshTimeout = undefined;
  }
};

const scheduleTokenRefresh = (token: KeycloakToken | null) => {
  const now = new Date().getTime();
  const timeout = (token?.expiry || now) - 60_000 - now;

  // implicitly validate the provided token
  const refreshToken = token?.refresh_token;
  if (!refreshToken || timeout <= 0) return false;

  // schedule the refresh
  console.info(`[keycloak-lib] Token refresh in ${timeout}ms`);
  keycloakState.refreshTimeout = setTimeout(
    () =>
      handleTokenPromise(
        fetchTokenWithRefreshToken(refreshToken),
        'scheduled refresh'
      ),
    timeout
  );

  return true;
};

const refreshTokenWhenNeeded = async (token: KeycloakToken | null) => {
  // cancel the currently scheduled refresh
  cancelTokenRefresh();

  // schedule default refetch if the token is possible
  if (scheduleTokenRefresh(token)) return token;

  const refreshToken = token?.refresh_token;
  const lock = localStorage.getItem(keycloakConfig.lockKey);
  if (lock && Date.now() - parseInt(lock) < 5_000)
    // restore from storage if another tab has recently received a new token
    token = await restoreTokenFromStorage();
  else if (refreshToken) {
    // refresh the token immediately
    localStorage.setItem(keycloakConfig.lockKey, Date.now().toString());
    console.info(`[keycloak-lib] Token is expired`);
    token = await fetchTokenWithRefreshToken(refreshToken);
  } else {
    // token outdated and refresh not possible
    console.warn('[keycloak-lib] No refresh token');
    token = null;
  }

  // ensure the next refresh is scheduled
  if (!scheduleTokenRefresh(token)) {
    console.info('[keycloak-lib] Login expired');
    keycloakState.callback?.(null, new Error('Login expired'));
  }

  return logToken(withExpiry(token), 'refresh');
};

const restoreTokenFromStorage = async () => {
  let token: KeycloakToken | null = null;
  try {
    const jsonToken = localStorage.getItem(keycloakConfig.tokenKey);
    token = jsonToken && JSON.parse(jsonToken);
    console.info('[keycloak-lib] Restoring token from storage');
  } catch (error) {
    console.warn('[keycloak-lib] Failed to restore token from storage', error);
  }

  return token;
};

export const keycloakSignInWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  await handleTokenPromise(
    fetchTokenWithEmailAndPassword(email, password),
    'login'
  );
};

export const keycloakSignOut = async () => {
  await handleTokenPromise(fetchLogout(), 'logout');
};

/**
 * Initialize keycloak and restore the login state
 */
export const initializeKeycloak = async (
  clientId: string,
  baseUrl: string,
  realm: string,
  callback?: KeycloakTokenCallback
) => {
  keycloakConfig.tokenKey = `${TOKEN_KEY}:${clientId}:${realm}`;
  keycloakConfig.lockKey = `${LOCK_KEY}:${clientId}:${realm}`;

  keycloakConfig.clientId = clientId;
  keycloakConfig.baseUrl = baseUrl;
  keycloakConfig.realm = realm;

  keycloakState.callback = callback;

  await handleTokenPromise(restoreTokenFromStorage(), 'storage');

  // watch for storage changes (i.e. from other tabs)
  if (!keycloakConfig.initialized)
    window.addEventListener('storage', (event) => {
      if (event.key === keycloakConfig.tokenKey && event.newValue)
        handleTokenPromise(restoreTokenFromStorage(), 'storage notification');
    });

  keycloakConfig.initialized = true;
};
